/**
 * Robust Assignment Search Service
 * Implements hierarchical search strategy with fallback mechanisms
 * Performance optimized with caching and parallel search capabilities
 */

import { fetchEngagementDetails } from './engagement';
import { extractFileIdFromText } from '../utils/emailAssignmentExtractor';

// Cache for search results to improve performance
const searchCache = new Map();
const CACHE_TIMEOUT = 5 * 60 * 1000; // 5 minutes

/**
 * Enhanced search strategies with scoring system
 */
const SEARCH_STRATEGIES = {
  FILE_ID: {
    priority: 1,
    weight: 100,
    searchType: 'exact',
    extractors: [
      // Removed: (text) => extractFileIdFromText(text) - this expects email text, not assignment object
      // Only keep extractors that work with assignment objects
      (assignment) => assignment?.code,
      (assignment) => assignment?.fileId,
      (assignment) => assignment?.userFriendlyId
    ],
    matchers: [
      (searchTerm, assignment) => {
        // Ensure searchTerm is a string before comparison
        if (!searchTerm || typeof searchTerm !== 'string') return false;
        
        const codes = [assignment?.code, assignment?.fileId, assignment?.userFriendlyId]
          .filter(Boolean)
          .filter(code => typeof code === 'string');
        
        return codes.some(code => code.toLowerCase() === searchTerm.toLowerCase());
      }
    ]
  },

  PROPERTY_NAME: {
    priority: 2,
    weight: 80,
    searchType: 'fuzzy',
    extractors: [
      (assignment) => assignment?.propertyAddress?.propertyName,
      (assignment) => assignment?.propertyName
    ],
    matchers: [
      (searchTerm, assignment) => {
        // Ensure searchTerm is a string
        if (!searchTerm || typeof searchTerm !== 'string') return false;
        
        const names = [
          assignment?.propertyAddress?.propertyName,
          assignment?.propertyName
        ].filter(Boolean).filter(name => typeof name === 'string');
        
        return names.some(name => {
          const normalizedName = name.toLowerCase().trim();
          const normalizedSearch = searchTerm.toLowerCase().trim();
          
          // Exact match
          if (normalizedName === normalizedSearch) return true;
          
          // Contains match
          if (normalizedName.includes(normalizedSearch) || normalizedSearch.includes(normalizedName)) return true;
          
          // Word-based fuzzy matching
          const nameWords = normalizedName.split(/\s+/);
          const searchWords = normalizedSearch.split(/\s+/);
          
          const matchingWords = searchWords.filter(searchWord =>
            nameWords.some(nameWord => 
              nameWord.includes(searchWord) || searchWord.includes(nameWord)
            )
          );
          
          return matchingWords.length >= Math.min(2, searchWords.length);
        });
      }
    ]
  },

  PROPERTY_ADDRESS: {
    priority: 3,
    weight: 70,
    searchType: 'fuzzy',
    extractors: [
      (assignment) => {
        const pa = assignment?.propertyAddress;
        if (!pa) return null;
        
        const parts = [
          pa.address1?.trim(),
          pa.address2?.trim(),
          pa.city?.trim(),
          pa.state?.name?.trim(),
          pa.postalCode?.trim(),
        ].filter(Boolean).filter(part => typeof part === 'string');
        
        return parts.length > 0 ? parts.join(', ') : null;
      }
    ],
    matchers: [
      (searchTerm, assignment) => {
        // Ensure searchTerm is a string
        if (!searchTerm || typeof searchTerm !== 'string') return false;
        
        const pa = assignment?.propertyAddress;
        if (!pa) return false;
        
        const addressParts = [
          pa.address1,
          pa.address2,
          pa.city,
          pa.state?.name,
          pa.postalCode
        ].filter(Boolean).filter(part => typeof part === 'string');
        
        if (addressParts.length === 0) return false;
        
        const fullAddress = addressParts.join(' ').toLowerCase();
        const normalizedSearch = searchTerm.toLowerCase().trim();
        
        // Direct substring match for short searches
        if (fullAddress.includes(normalizedSearch) || normalizedSearch.includes(fullAddress)) {
          return true;
        }
        
        // Check individual address components for partial matches
        const searchWords = normalizedSearch.split(/\s+/).filter(w => w.length > 0);
        const addressWords = fullAddress.split(/\s+/).filter(w => w.length > 0);
        
        if (searchWords.length === 0) return false;
        
        const matchingWords = searchWords.filter(searchWord =>
          addressWords.some(addressWord => 
            addressWord.includes(searchWord) || searchWord.includes(addressWord)
          )
        );
        
        // For short searches (1-2 words), require at least 1 match
        // For longer searches, require at least 50% match
        if (searchWords.length <= 2) {
          return matchingWords.length >= 1;
        } else {
          return matchingWords.length >= Math.ceil(searchWords.length * 0.5);
        }
      }
    ]
  },

  CLIENT_DETAILS: {
    priority: 4,
    weight: 60,
    searchType: 'fuzzy',
    extractors: [
      (assignment) => assignment?.client?.clientName,
      (assignment) => assignment?.clientName,
      (assignment) => assignment?.client?.companyName
    ],
    matchers: [
      (searchTerm, assignment) => {
        // Ensure searchTerm is a string
        if (!searchTerm || typeof searchTerm !== 'string') return false;
        
        const clientNames = [
          assignment?.client?.clientName,
          assignment?.clientName,
          assignment?.client?.companyName
        ].filter(Boolean).filter(name => typeof name === 'string');
        
        return clientNames.some(name => {
          const normalizedName = name.toLowerCase().trim();
          const normalizedSearch = searchTerm.toLowerCase().trim();
          
          return normalizedName.includes(normalizedSearch) || normalizedSearch.includes(normalizedName);
        });
      }
    ]
  }
};

/**
 * Advanced Assignment Search Service Class
 */
class AssignmentSearchService {
  constructor() {
    this.searchHistory = new Map();
    this.performanceMetrics = {
      searchCount: 0,
      cacheHits: 0,
      averageSearchTime: 0
    };
  }

  /**
   * Main search method with hierarchical strategy
   */
  async searchAssignments(searchCriteria, options = {}) {
    const startTime = Date.now();
    const {
      maxResults = 50,
      includeScoring = true,
      enableCache = true,
      parallelSearch = true,
      fallbackToFuzzy = true
    } = options;

    try {
      const normalizedCriteria = this.normalizeCriteria(searchCriteria);
      if (!normalizedCriteria || Object.keys(normalizedCriteria).length === 0) {
        return { assignments: [], searchStrategy: null, score: 0 };
      }

      const cacheKey = this.generateCacheKey(normalizedCriteria);
      if (enableCache && this.isCacheValid(cacheKey)) {
        this.performanceMetrics.cacheHits++;
        return searchCache.get(cacheKey).result;
      }

      let bestResult = null;
      let bestScore = 0;
      let bestStrategy = null;

      const strategies = Object.entries(SEARCH_STRATEGIES)
        .sort(([,a], [,b]) => a.priority - b.priority);

      for (const [strategyName, strategy] of strategies) {
        const searchTerm = this.extractSearchTerm(normalizedCriteria, strategyName);
        if (!searchTerm) continue;

        try {
          const result = await this.executeSearchStrategy(
            searchTerm, 
            strategy, 
            { maxResults, includeScoring }
          );

          if (result && result.assignments.length > 0) {
            const avgScore = includeScoring ? this.calculateAverageScore(result.assignments) : 1;

            if (strategy.searchType === 'exact' && avgScore >= 0.9) {
              bestResult = result;
              bestStrategy = strategyName;
              bestScore = avgScore;
              break;
            }

            if (avgScore > bestScore) {
              bestResult = result;
              bestStrategy = strategyName;
              bestScore = avgScore;
            }
          }
        } catch (error) {
          continue;
        }
      }

      if (!bestResult && fallbackToFuzzy) {
        bestResult = await this.executeFallbackSearch(normalizedCriteria, options);
        bestStrategy = 'FUZZY_FALLBACK';
      }

      const finalResult = {
        assignments: bestResult?.assignments || [],
        searchStrategy: bestStrategy,
        score: bestScore,
        searchTime: Date.now() - startTime,
        criteria: normalizedCriteria
      };

      if (enableCache && finalResult.assignments.length > 0) {
        searchCache.set(cacheKey, {
          result: finalResult,
          timestamp: Date.now()
        });
      }

      this.updateMetrics(Date.now() - startTime);

      return finalResult;

    } catch (error) {
      return {
        assignments: [],
        searchStrategy: 'ERROR',
        score: 0,
        error: error.message,
        searchTime: Date.now() - startTime
      };
    }
  }

  /**
   * Execute individual search strategy
   */
  async executeSearchStrategy(searchTerm, strategy, options = {}) {
    const { maxResults = 50, includeScoring = true } = options;

    // Fetch assignments from API
    let assignments = await this.fetchAssignmentsFromAPI(searchTerm, maxResults);
    
    if (!assignments || assignments.length === 0) {
      return null;
    }

    // Apply strategy-specific matching and scoring
    assignments = assignments.filter(assignment => {
      return strategy.matchers.some(matcher => matcher(searchTerm, assignment));
    });

    if (includeScoring) {
      assignments = assignments.map(assignment => ({
        ...assignment,
        searchScore: this.calculateAssignmentScore(searchTerm, assignment, strategy),
        matchStrategy: strategy
      })).sort((a, b) => b.searchScore - a.searchScore);
    }

    return {
      assignments: assignments.slice(0, maxResults),
      strategy: strategy,
      searchTerm
    };
  }

  /**
   * Fallback comprehensive fuzzy search
   */
  async executeFallbackSearch(criteria, options = {}) {
    const { maxResults = 50 } = options;
    
    // Combine all criteria into comprehensive search terms
    const searchTerms = Object.values(criteria).filter(Boolean);
    if (searchTerms.length === 0) return null;

    // Try broader API search with combined terms
    const combinedSearch = searchTerms.join(' ');
    
    let assignments = await this.fetchAssignmentsFromAPI(combinedSearch, maxResults * 2);
    
    if (!assignments || assignments.length === 0) {
      return null;
    }

    assignments = assignments.map(assignment => {
      let totalScore = 0;
      let matchCount = 0;

      Object.entries(SEARCH_STRATEGIES).forEach(([strategyName, strategy]) => {
        const searchTerm = criteria[this.getStrategyKey(strategyName)];
        if (!searchTerm) return;

        const matches = strategy.matchers.some(matcher => matcher(searchTerm, assignment));
        if (matches) {
          const score = this.calculateAssignmentScore(searchTerm, assignment, strategy);
          totalScore += score * strategy.weight;
          matchCount++;
        }
      });

      return {
        ...assignment,
        searchScore: matchCount > 0 ? totalScore / matchCount : 0,
        matchCount
      };
    });

    assignments = assignments
      .filter(a => a.searchScore > 0.3)
      .sort((a, b) => b.searchScore - a.searchScore)
      .slice(0, maxResults);

    return {
      assignments,
      strategy: 'fuzzy_fallback'
    };
  }

  /**
   * Calculate assignment relevance score
   */
  calculateAssignmentScore(searchTerm, assignment, strategy) {
    // Ensure searchTerm is a string
    if (!searchTerm || typeof searchTerm !== 'string') return 0;
    
    let score = 0;
    const normalizedSearch = searchTerm.toLowerCase().trim();

    // Extract values for comparison
    const extractedValues = strategy.extractors
      .map(extractor => extractor(assignment))
      .filter(Boolean)
      .filter(value => typeof value === 'string'); // Ensure all values are strings

    for (const value of extractedValues) {
      const normalizedValue = value.toLowerCase().trim();
      
      if (strategy.searchType === 'exact') {
        if (normalizedValue === normalizedSearch) {
          score = 1.0;
          break;
        } else if (normalizedValue.includes(normalizedSearch)) {
          score = Math.max(score, 0.8);
        }
      } else {
        // Fuzzy scoring
        const similarity = this.calculateStringSimilarity(normalizedSearch, normalizedValue);
        score = Math.max(score, similarity);
      }
    }

    return Math.min(score, 1.0);
  }

  /**
   * String similarity calculation using Jaccard coefficient
   */
  calculateStringSimilarity(str1, str2) {
    // Ensure both parameters are strings
    if (!str1 || !str2 || typeof str1 !== 'string' || typeof str2 !== 'string') {
      return 0;
    }
    
    const words1 = new Set(str1.split(/\s+/));
    const words2 = new Set(str2.split(/\s+/));
    
    const intersection = new Set([...words1].filter(x => words2.has(x)));
    const union = new Set([...words1, ...words2]);
    
    return union.size === 0 ? 0 : intersection.size / union.size;
  }

  /**
   * Fetch assignments from API with caching
   */
  async fetchAssignmentsFromAPI(searchTerm, maxResults = 50) {
    try {
      const results = await fetchEngagementDetails(1, maxResults, searchTerm);
      const assignments = Array.isArray(results) ? results : [];
      
      // Normalize assignment data to match store structure
      return assignments.map(item => ({
        id: item.id,
        code: item?.fileId,
        trackerEngagementStatus: item.trackerEngagementStatus,
        assignmentStatus: item.assignmentStatus,
        assignmentSource: item.assignmentSource,
        propertyName: item.propertyAddress?.propertyName,
        propertyType: item.propertyType?.name,
        primaryAppraiser: item.primaryValuer?.fullName,
        originator: item.jobOriginator?.fullName,
        client: item.client?.clientName,
        clientDetails: item.client,
        propertyAddress: item.propertyAddress,
        address: `${item.propertyAddress?.propertyName} ${item.propertyAddress?.city}`,
        workFileLink: item.workFileLink,
        viewEventId: item.viewEventId,
        sourceSystemId: item.sourceSystemId,
        clientReferenceNumber: item.clientReferenceNumber,
        objectType: item.objectType,
        deadline: {
          assignmentDueDate: item.deadline?.assignmentDueDate || item.assignmentDueDate || item.dueDate
        },
        primaryValuerEmail: item.primaryValuer?.emailAddress,
        originatorEmail: item.jobOriginator?.emailAddress,
        primaryValuerEmailAddress: item.primaryValuer?.emailAddress,
        valuers: item.valuers || [],
        createdTimestamp: item.createdTimestamp,
        userFriendlyId: item.engagementId || item.userFriendlyId || item.code || item.fileId,
        subjectProperty: item.subjectProperty,
        // Keep original fields for compatibility
        ...item
      }));
    } catch (error) {
      return [];
    }
  }

  /**
   * Normalize search criteria
   */
  normalizeCriteria(criteria) {
    const normalized = {};
    
    // Ensure all values are properly converted to strings and validated
    if (criteria.fileId) {
      const fileIdValue = criteria.fileId;
      normalized.fileId = (typeof fileIdValue === 'string' ? fileIdValue : String(fileIdValue)).trim();
    }
    if (criteria.assignmentId) {
      const assignmentIdValue = criteria.assignmentId;
      normalized.assignmentId = (typeof assignmentIdValue === 'string' ? assignmentIdValue : String(assignmentIdValue)).trim();
    }
    if (criteria.propertyName && typeof criteria.propertyName === 'string') {
      normalized.propertyName = criteria.propertyName.trim();
    }
    if (criteria.propertyAddress && typeof criteria.propertyAddress === 'string') {
      normalized.propertyAddress = criteria.propertyAddress.trim();
    }
    if (criteria.clientName && typeof criteria.clientName === 'string') {
      normalized.clientName = criteria.clientName.trim();
    }

    return normalized;
  }

  /**
   * Extract search term for specific strategy
   */
  extractSearchTerm(criteria, strategyName) {
    const keyMap = {
      'FILE_ID': 'fileId',
      'ASSIGNMENT_ID': 'assignmentId',
      'PROPERTY_NAME': 'propertyName',
      'PROPERTY_ADDRESS': 'propertyAddress',
      'CLIENT_DETAILS': 'clientName'
    };
    
    const term = criteria[keyMap[strategyName]];
    // Ensure we return a string or null
    return (term && typeof term === 'string') ? term : null;
  }

  /**
   * Get strategy key for criteria mapping
   */
  getStrategyKey(strategyName) {
    const keyMap = {
      'FILE_ID': 'fileId',
      'ASSIGNMENT_ID': 'assignmentId',
      'PROPERTY_NAME': 'propertyName',
      'PROPERTY_ADDRESS': 'propertyAddress',
      'CLIENT_DETAILS': 'clientName'
    };
    
    return keyMap[strategyName];
  }

  /**
   * Calculate average score for result set
   */
  calculateAverageScore(assignments) {
    if (!assignments || assignments.length === 0) return 0;
    const scores = assignments.map(a => a.searchScore || 0);
    return scores.reduce((sum, score) => sum + score, 0) / scores.length;
  }

  /**
   * Cache management
   */
  generateCacheKey(criteria) {
    return JSON.stringify(criteria);
  }

  isCacheValid(key) {
    const cached = searchCache.get(key);
    return cached && (Date.now() - cached.timestamp < CACHE_TIMEOUT);
  }

  clearCache() {
    searchCache.clear();
  }

  /**
   * Performance metrics
   */
  updateMetrics(searchTime) {
    this.performanceMetrics.searchCount++;
    const { searchCount, averageSearchTime } = this.performanceMetrics;
    this.performanceMetrics.averageSearchTime = 
      ((averageSearchTime * (searchCount - 1)) + searchTime) / searchCount;
  }

  getPerformanceMetrics() {
    return { ...this.performanceMetrics };
  }
}

// Singleton instance
const assignmentSearchService = new AssignmentSearchService();

/**
 * Convenient wrapper functions
 */

/**
 * Search with automatic email extraction
 */
export async function searchAssignmentsFromEmail(emailData, options = {}) {
  const criteria = {};
  const emailText = `${emailData?.subject || ''} ${emailData?.body || ''}`;
  
  console.log('🔍 Starting email search with:', {
    subject: emailData?.subject,
    bodyLength: emailData?.body?.length
  });
  
  // 1. Extract File ID or Engagement ID from email
  const extractedFileId = extractFileIdFromText(emailText);
  if (extractedFileId) {
    criteria.fileId = extractedFileId;
    console.log('✅ Extracted File ID (with hyphen):', extractedFileId);
  } else {
    // Try to extract Engagement ID with label (e.g., "Engagement ID: CB24US001632")
    const engagementIdWithLabelPattern = /(?:Engagement\s*ID|Engagement\s*Id)\s*[:\-–]\s*(CB\d{2}US\d{6})\b/i;
    let engagementMatch = emailText.match(engagementIdWithLabelPattern);
    
    if (!engagementMatch) {
      // Try without label - just the pattern (e.g., CB24US001632)
      const engagementIdPattern = /\b(CB\d{2}US\d{6})\b/i;
      engagementMatch = emailText.match(engagementIdPattern);
    }
    
    if (engagementMatch) {
      criteria.fileId = engagementMatch[1];
      console.log('✅ Extracted Engagement ID:', engagementMatch[1]);
    } else {
      console.log('❌ No File ID or Engagement ID found');
    }
  }

  // 2. Extract Property Name
  let propertyNameFound = false;
  
  const propertyNamePatterns = [
    /Property\s*Name\s*[:\-–]\s*([^\n\r<|]+)/i,
    /Propert\s*Name\s*[:\-–]\s*([^\n\r<|]+)/i,
    /Proprty\s*Name\s*[:\-–]\s*([^\n\r<|]+)/i,
    /Property\s*[:\-–]\s*([^\n\r<|]+)/i,
    /Prop[\s.]*Name\s*[:\-–]\s*([^\n\r<|]+)/i,
    /Name\s+of\s+Property\s*[:\-–]\s*([^\n\r<|]+)/i
  ];
  
  if (emailData?.subject && !propertyNameFound) {
    for (const pattern of propertyNamePatterns) {
      const subjectMatch = emailData.subject.match(pattern);
      if (subjectMatch && subjectMatch[1]) {
        const propertyName = subjectMatch[1].trim();
        if (propertyName && propertyName.length > 2 && propertyName.length < 100) {
          criteria.propertyName = propertyName;
          propertyNameFound = true;
          break;
        }
      }
    }
  }
  
  if (emailData?.body && !propertyNameFound) {
    for (const pattern of propertyNamePatterns) {
      const bodyMatch = emailData.body.match(pattern);
      if (bodyMatch && bodyMatch[1]) {
        const propertyName = bodyMatch[1].trim();
        if (propertyName && propertyName.length > 2 && propertyName.length < 100) {
          criteria.propertyName = propertyName;
          propertyNameFound = true;
          break;
        }
      }
    }
  }
  
  if (!propertyNameFound && emailData?.subject) {
    const subjectText = emailData.subject;
    
    const appraisalPatterns = [
      /Appraisal\s+of\s+([^|]+)/i,
      /Inspection\s+of\s+([^|]+)/i,
      /Valuation\s+of\s+([^|]+)/i,
      /Assessment\s+of\s+([^|]+)/i,
      /Review\s+of\s+([^|]+)/i
    ];
    
    for (const pattern of appraisalPatterns) {
      const match = subjectText.match(pattern);
      if (match && match[1]) {
        const propertyName = match[1].trim();
        const looksLikeAddress = /\d+.*(?:street|st|avenue|ave|road|rd|lane|ln|drive|dr|court|ct|blvd|way)/i.test(propertyName);
        
        if (propertyName && propertyName.length > 2 && !looksLikeAddress) {
          criteria.propertyName = propertyName;
          propertyNameFound = true;
          break;
        }
      }
    }
  }
  
  if (!propertyNameFound && emailData?.subject) {
    const subjectText = emailData.subject;
    
    if (subjectText.includes('|')) {
      const beforePipe = subjectText.split('|')[0].trim();
      const cleanedText = beforePipe.replace(/^(re:|fwd?:|fw:|appraisal of|inspection of|valuation of)\s*/i, '').trim();
      
      const genericPhrases = [
        /^(testing|test|meeting|sprint|planning|discussion|update|review|feedback|follow[\s-]?up)/i,
        /^(hi|hello|hey|good morning|good afternoon|good evening|greetings)/i,
        /^(fyi|for your information|urgent|important|reminder|request|question|inquiry)/i,
        /^(schedule|scheduling|confirm|confirmation|please|thanks|thank you)/i,
        /^(daily|weekly|monthly|status|report|summary)/i
      ];
      
      const isGeneric = genericPhrases.some(pattern => pattern.test(cleanedText));
      const looksLikeAddress = /\d+.*(?:street|st|avenue|ave|road|rd|lane|ln|drive|dr|court|ct|blvd|way)/i.test(cleanedText);
      const tooShort = cleanedText.length <= 3;
      const hasEmailKeywords = /^(from|to|cc|bcc|sent|date|subject):/i.test(cleanedText);
      
      if (cleanedText && !isGeneric && !looksLikeAddress && !tooShort && !hasEmailKeywords) {
        criteria.propertyName = cleanedText;
        propertyNameFound = true;
      }
    }
  }

  // 3. Extract Property Address - ENHANCED FOR STRUCTURED EMAIL FORMATS
  let propertyAddressFound = false;
  
  // PRIORITY 1: Look for explicit "Address:" or "Property Address:" label in subject and body
  // Pattern: "Property Address: Sky Blue Path, 87745, Dallas, Georgia, 30157 | Property Type: Office N/A"
  const addressLabelPatterns = [
    /(?:Property\s*Address|Address)\s*[:\-–]\s*([^|<\n\r]+?)(?:\s*\||<|$)/i,
  ];
  
  // Check body first for labeled addresses (more reliable than subject)
  if (emailData?.body && !propertyAddressFound) {
    for (const pattern of addressLabelPatterns) {
      const bodyMatch = emailData.body.match(pattern);
      if (bodyMatch && bodyMatch[1]) {
        let address = bodyMatch[1].trim();
        
        // Skip if it's just a number or URL-like text
        if (/^\d+$/.test(address) || address.includes('http') || address.length < 10) {
          continue;
        }
        
        // Skip if it contains HTML tags
        if (address.includes('<') || address.includes('>')) {
          continue;
        }
        
        // Validate it looks like a real address
        const hasNumbers = /\d/.test(address);
        const hasStreetWords = /(?:path|street|st|avenue|ave|road|rd|lane|ln|drive|dr|court|ct|boulevard|blvd|place|pl|way)/i.test(address);
        const hasComma = address.includes(',');
        
        if (address.length >= 10 && address.length < 200 && hasNumbers && (hasStreetWords || hasComma)) {
          criteria.propertyAddress = address;
          propertyAddressFound = true;
          console.log('✅ Extracted address from body:', address);
          break;
        }
      }
    }
  }
  
  // Check subject if not found in body
  if (emailData?.subject && !propertyAddressFound) {
    for (const pattern of addressLabelPatterns) {
      const subjectMatch = emailData.subject.match(pattern);
      if (subjectMatch && subjectMatch[1]) {
        let address = subjectMatch[1].trim();
        
        // Skip if it's just a number or URL-like text
        if (/^\d+$/.test(address) || address.includes('http') || address.length < 10) {
          continue;
        }
        
        // Skip if it contains HTML tags
        if (address.includes('<') || address.includes('>')) {
          continue;
        }
        
        // Validate it looks like a real address
        const hasNumbers = /\d/.test(address);
        const hasStreetWords = /(?:path|street|st|avenue|ave|road|rd|lane|ln|drive|dr|court|ct|boulevard|blvd|place|pl|way)/i.test(address);
        const hasComma = address.includes(',');
        
        if (address.length >= 10 && address.length < 200 && hasNumbers && (hasStreetWords || hasComma)) {
          criteria.propertyAddress = address;
          propertyAddressFound = true;
          console.log('✅ Extracted address from subject:', address);
          break;
        }
      }
    }
  }
  
  // PRIORITY 2: Detect addresses with City, State, ZIP pattern (e.g., "2801 S Decker Lake Dr, Salt Lake City, Utah 84119")
  if (!propertyAddressFound && emailData?.subject) {
    // Clean subject first (remove FW:, RE:, etc.)
    const cleanSubject = emailData.subject.replace(/^(re:|fwd?:|fw:)\s*/i, '').trim();
    
    // Pattern: Street Number + Direction? + Street Name + Dr/St/Ave/etc + Comma + City + Comma + State + ZIP
    // Matches: "2801 S Decker Lake Dr, Salt Lake City, Utah 84119"
    // Matches: "701 E 29th Street, Paterson, NJ 07504"
    const cityStateZipPattern = /^(\d+[-\s]?\d*\s+(?:[NESW]\s+)?[A-Za-z0-9\s]+(?:Street|St|Avenue|Ave|Road|Rd|Lane|Ln|Drive|Dr|Court|Ct|Boulevard|Blvd|Place|Pl|Way|Circle|Cir)(?:,|\s)+[A-Z][a-z]+(?:\s+[A-Z][a-z]+)*(?:,|\s)+[A-Z][a-z]{2,20}\s+\d{5}(?:-\d{4})?)/i;
    
    const match = cleanSubject.match(cityStateZipPattern);
    
    if (match && match[1]) {
      const address = match[1].trim();
      if (address.length > 10 && address.length < 200) {
        criteria.propertyAddress = address;
        propertyAddressFound = true;
      }
    }
  }
  
  // PRIORITY 3: Look for complete street addresses anywhere in subject (with city/state/zip)
  if (!propertyAddressFound && emailData?.subject) {
    const completeAddressPatterns = [
      // Address with state name spelled out and ZIP: "2801 S Decker Lake Dr, Salt Lake City, Utah 84119"
      /(\d+[-\s]?\d*\s+[NESW]?\s*[A-Za-z0-9\s]+(?:Street|St|Avenue|Ave|Road|Rd|Lane|Ln|Drive|Dr|Court|Ct|Boulevard|Blvd|Place|Pl|Way|Circle|Cir)[^,]*,\s*[A-Z][a-z]+(?:\s+[A-Z][a-z]+)*,\s*[A-Z][a-z]{1,20}\s+\d{5}(?:-\d{4})?)/i,
      // Address with state abbreviation and ZIP: "701 E 29th Street, Paterson, NJ 07504"
      /(\d+[-\s]?\d*\s+[NESW]?\s*\d*(?:st|nd|rd|th)?\s+[A-Za-z\s]+(?:Street|St|Avenue|Ave|Road|Rd|Lane|Ln|Drive|Dr|Court|Ct|Boulevard|Blvd|Place|Pl|Way|Circle|Cir)[^,]*,\s*[A-Z][a-z]+(?:\s+[A-Z][a-z]+)*,\s*[A-Z]{2}\s+\d{5}(?:-\d{4})?)/i
    ];
    
    const cleanSubject = emailData.subject.replace(/^(re:|fwd?:|fw:)\s*/i, '').trim();
    for (const pattern of completeAddressPatterns) {
      const match = cleanSubject.match(pattern);
      if (match && match[1]) {
        const address = match[1].trim();
        if (address.length > 10 && address.length < 200) {
          criteria.propertyAddress = address;
          propertyAddressFound = true;
          break;
        }
      }
    }
  }
  
  // PRIORITY 4: Look for addresses in structured format (e.g., before "Transaction Type:")
  if (!propertyAddressFound && emailData?.body) {
    const structuredAddressPattern = /^([0-9]+-?[0-9]*\s+[^,\n]+(?:Street|St|Avenue|Ave|Road|Rd|Lane|Ln|Drive|Dr|Court|Ct|Boulevard|Blvd|Place|Pl)\s+[A-Za-z\s]+\s+[A-Z]{2}\s+\d{5})\s*[\n\r]+(?:Transaction Type|Property Type|Product Needed)/mi;
    const structuredMatch = emailData.body.match(structuredAddressPattern);
    
    if (structuredMatch && structuredMatch[1]) {
      const address = structuredMatch[1].trim();
      if (address.length > 10 && address.length < 200) {
        criteria.propertyAddress = address;
        propertyAddressFound = true;
      }
    }
  }
  
  // PRIORITY 5: Look for address patterns in subject (after "|")
  if (!propertyAddressFound && emailData?.subject && emailData.subject.includes('|')) {
    const afterPipe = emailData.subject.split('|')[1]?.trim();
    if (afterPipe && afterPipe.match(/\d+.*\w+.*\w+/)) {
      criteria.propertyAddress = afterPipe;
      propertyAddressFound = true;
    }
  }
  
  // PRIORITY 6: Look for complete street addresses with city, state, zip in body
  if (!propertyAddressFound) {
    const addressPatterns = [
      // Address with explicit label
      /(?:property at|located at|address[:\s;]+|appraise the property at)\s*([^.!?\n]+(?:street|st|avenue|ave|road|rd|lane|ln|drive|dr|court|ct|boulevard|blvd|place|pl|township)[^.!?\n]*)/i,
      // Complete address: Number + Street Name + City + State (2 letters) + ZIP (5 digits)
      // Matches: "701 E 29th Street Paterson NJ 07504"
      /\b(\d+-?\d*\s+[NESW]?\s*\d*(?:st|nd|rd|th)?\s+(?:[A-Z][a-z]+\s+)*(?:Street|St|Avenue|Ave|Road|Rd|Lane|Ln|Drive|Dr|Court|Ct|Boulevard|Blvd|Place|Pl)\s+[A-Z][a-z]+(?:\s+[A-Z][a-z]+)*\s+[A-Z]{2}\s+\d{5})\b/i,
      // Street address with numbered ranges (e.g., "183-189 18th Avenue")
      /\b(\d+-\d+\s+\d+(?:st|nd|rd|th)\s+(?:Street|St|Avenue|Ave|Road|Rd|Lane|Ln|Drive|Dr|Court|Ct|Boulevard|Blvd|Place|Pl)\s+[A-Z][a-z]+(?:\s+[A-Z][a-z]+)*\s+[A-Z]{2}\s+\d{5})\b/i,
      // General pattern with city, state, zip
      /([A-Z][a-zA-Z0-9\s_-]+(?:street|st|avenue|ave|road|rd|lane|ln|drive|dr|court|ct|boulevard|blvd|place|pl|township)[^,\n]*(?:,\s*)?[A-Z][a-z]+(?:\s+[A-Z][a-z]+)*\s+[A-Z]{2}\s+\d{5})/i,
      // Numbered address pattern
      /(\d+\s+[^,\n]+(?:street|st|avenue|ave|road|rd|lane|ln|drive|dr|court|ct|boulevard|blvd|place|pl|township)[^,\n]*(?:,\s*[^,\n]+)*(?:,?\s*[A-Z]{2}\s*\d{5})?)/i
    ];
    
    // Try subject first with all patterns
    if (emailData?.subject) {
      for (const pattern of addressPatterns) {
        const match = emailData.subject.match(pattern);
        if (match) {
          let address = match[1] || match[0];
          address = address
            .replace(/^(property at|located at|address[:\s;]+|appraise the property at)\s*/i, '')
            .trim();
          
          if (address && address.length > 10 && address.length < 200) {
            const hasNumber = /\d/.test(address);
            const hasStreetType = /street|st|avenue|ave|road|rd|lane|ln|drive|dr|court|ct|boulevard|blvd|place|pl/i.test(address);
            
            if (hasNumber && hasStreetType) {
              criteria.propertyAddress = address;
              propertyAddressFound = true;
              break;
            }
          }
        }
      }
    }
    
    // Try body if not found in subject
    if (!propertyAddressFound && emailData?.body) {
      for (const pattern of addressPatterns) {
        const match = emailData.body.match(pattern);
        if (match) {
          let address = match[1] || match[0];
          address = address
            .replace(/^(property at|located at|address[:\s;]+|appraise the property at)\s*/i, '')
            .trim();
          
          if (address && address.length > 10 && address.length < 200) {
            const hasNumber = /\d/.test(address);
            const hasStreetType = /street|st|avenue|ave|road|rd|lane|ln|drive|dr|court|ct|boulevard|blvd|place|pl/i.test(address);
            
            if (hasNumber && hasStreetType) {
              criteria.propertyAddress = address;
              propertyAddressFound = true;
              break;
            }
          }
        }
      }
    }
    
    // Fallback: check combined emailText if still not found
    if (!propertyAddressFound) {
      for (const pattern of addressPatterns) {
        const match = emailText.match(pattern);
        if (match) {
          let address = match[1] || match[0];
          address = address
            .replace(/^(property at|located at|address[:\s;]+|appraise the property at)\s*/i, '')
            .trim();
          
          if (address && address.length > 10 && address.length < 200) {
            const hasNumber = /\d/.test(address);
            const hasStreetType = /street|st|avenue|ave|road|rd|lane|ln|drive|dr|court|ct|boulevard|blvd|place|pl/i.test(address);
            
            if (hasNumber && hasStreetType) {
              criteria.propertyAddress = address;
              propertyAddressFound = true;
              break;
            }
          }
        }
      }
    }
  }

  // 4. Extract Client Details from email
  if (emailData?.body) {
    let clientFound = false;
    
    const signatureClient = extractClientFromSignature(emailData.body);
    if (signatureClient) {
      criteria.clientName = signatureClient;
      clientFound = true;
    }
    
    if (!clientFound) {
      const clientPatterns = [
        /(?:for\s+|behalf of\s+|representing\s+)([A-Z][a-zA-Z\s&,.-]+(?:Inc\.?|LLC|L\.L\.C\.|Corp\.?|Corporation|Company|Co\.|Ltd\.?|Limited|LP|L\.P\.|LLP|L\.L\.P\.))/i,
        /(?:client[:\s]+)([A-Z][a-zA-Z\s&,.-]+(?:Inc\.?|LLC|L\.L\.C\.|Corp\.?|Corporation|Company|Co\.|Ltd\.?|Limited|LP|L\.P\.|LLP|L\.L\.P\.))/i,
        /(?:client[:\s]+)([A-Z][a-zA-Z\s&,.-]+)/i,
        /(?:on behalf of\s+)([A-Z][a-zA-Z\s&,.-]+)/i
      ];
      
      for (const pattern of clientPatterns) {
        const match = emailText.match(pattern);
        if (match) {
          const client = match[1].trim();
          if (client && client.length > 2 && client.length < 100) {
            criteria.clientName = client;
            clientFound = true;
            break;
          }
        }
      }
    }
    
    if (!clientFound && emailData?.subject) {
      const subjectClientPattern = /(?:for\s+|behalf of\s+)([A-Z][a-zA-Z\s&,.-]+(?:Inc\.?|LLC|Corp\.?|Corporation|Company|Co\.|Ltd\.?|Limited))/i;
      const subjectMatch = emailData.subject.match(subjectClientPattern);
      if (subjectMatch) {
        const client = subjectMatch[1].trim();
        if (client && client.length > 2 && client.length < 100) {
          criteria.clientName = client;
        }
      }
    }
  }
  
  if (Object.keys(criteria).length === 0) {
    console.log('❌ No search criteria found in email');
    return {
      assignments: [],
      searchStrategy: 'NO_CRITERIA_FOUND',
      score: 0,
      searchTime: 0,
      criteria: {}
    };
  }

  console.log('📋 Final search criteria:', criteria);

  // Clear cache before searching from email to avoid stale results
  assignmentSearchService.clearCache();

  // Perform initial search
  const result = await assignmentSearchService.searchAssignments(criteria, options);
  
  console.log('📊 Search result:', {
    foundCount: result.assignments?.length || 0,
    strategy: result.searchStrategy
  });
  
  // FALLBACK STRATEGY: If no results found and property address exists, try progressively simpler searches
  if (result.assignments.length === 0 && criteria.propertyAddress) {
    console.log('🔄 Trying address fallback strategies...');
    
    // FALLBACK 1: Try text before first comma (e.g., "Sky Blue Path" from "Sky Blue Path, 87745, Dallas, Georgia, 30157")
    if (criteria.propertyAddress.includes(',')) {
      const beforeFirstComma = criteria.propertyAddress.split(',')[0].trim();
      
      console.log('🔄 FALLBACK 1: Trying with first comma text:', beforeFirstComma);
      
      if (beforeFirstComma.length >= 5) {
        const fallbackCriteria1 = {
          propertyAddress: beforeFirstComma
        };
        
        const fallbackResult1 = await assignmentSearchService.searchAssignments(fallbackCriteria1, options);
        
        console.log('📊 FALLBACK 1 result:', {
          foundCount: fallbackResult1.assignments?.length || 0,
          strategy: fallbackResult1.searchStrategy
        });
        
        if (fallbackResult1.assignments.length > 0) {
          return {
            ...fallbackResult1,
            searchStrategy: `${fallbackResult1.searchStrategy}_FIRST_COMMA_FALLBACK`,
            originalCriteria: criteria,
            fallbackCriteria: fallbackCriteria1
          };
        }
      }
    }
    
    // FALLBACK 2: Try extracting structured street address (before city/state/zip)
    const streetAddressMatch = criteria.propertyAddress.match(/^(\d+[-\s]?\d*\s+(?:[NESW]\s+)?[A-Za-z0-9\s]+(?:Street|St|Avenue|Ave|Road|Rd|Lane|Ln|Drive|Dr|Court|Ct|Boulevard|Blvd|Place|Pl|Way|Circle|Cir|Path|Trail))/i);
    
    if (streetAddressMatch && streetAddressMatch[1]) {
      const streetAddressOnly = streetAddressMatch[1].trim();
      
      // Only retry if street address is different from what we already tried
      const alreadyTriedFirstComma = criteria.propertyAddress.includes(',') && 
                                      streetAddressOnly === criteria.propertyAddress.split(',')[0].trim();
      
      if (!alreadyTriedFirstComma && streetAddressOnly.length >= 5) {
        const fallbackCriteria2 = {
          propertyAddress: streetAddressOnly
        };
        
        const fallbackResult2 = await assignmentSearchService.searchAssignments(fallbackCriteria2, options);
        
        if (fallbackResult2.assignments.length > 0) {
          return {
            ...fallbackResult2,
            searchStrategy: `${fallbackResult2.searchStrategy}_STREET_ONLY_FALLBACK`,
            originalCriteria: criteria,
            fallbackCriteria: fallbackCriteria2
          };
        }
      }
    }
  }
  
  return result;
}

function extractClientFromSignature(emailBody) {
  if (!emailBody || typeof emailBody !== 'string') return null;
  
  const signatureDelimiters = [
    /--\s*<br[^>]*>/i,
    /<div[^>]*signature[^>]*>/i,
    /<br[^>]*>\s*<br[^>]*>\s*<br[^>]*>/i
  ];
  
  let signatureStartIndex = -1;
  for (const delimiter of signatureDelimiters) {
    const match = emailBody.match(delimiter);
    if (match) {
      signatureStartIndex = match.index;
      break;
    }
  }
  
  const signatureText = signatureStartIndex > 0 
    ? emailBody.substring(signatureStartIndex)
    : emailBody.substring(Math.max(0, emailBody.length - 600));
  
  // Pre-cleaning HTML search
  const preCleaningPatterns = [
    /(?:best\s+regards|regards|sincerely|thanks|thank\s+you)[\s\S]{0,50}?([A-Z][a-zA-Z]+(?:Test|Automation)[a-zA-Z\s]*(?:Client|Company|Corp|Inc|LLC))/i,
    /([A-Z][a-zA-Z]*Test[a-zA-Z\s]*(?:No|With)?Retainer[a-zA-Z\s]*Client)/i,
    /([A-Z][a-zA-Z]*Automation[a-zA-Z\s]*(?:Test|Client)[a-zA-Z\s]*)/i
  ];
  
  for (const pattern of preCleaningPatterns) {
    const match = signatureText.match(pattern);
    if (match && match[1]) {
      let companyName = match[1]
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      
      if (companyName.length >= 10 && companyName.length <= 100) {
        return companyName;
      }
    }
  }
  
  const cleanedSignature = signatureText
    .replace(/<[^>]+>/g, '\n')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/\r/g, '')
    .split('\n')
    .map(line => line.trim())
    .filter(line => line.length > 0);
  
  // Adjacent lines combination
  for (let i = 0; i < cleanedSignature.length - 1; i++) {
    const currentLine = cleanedSignature[i];
    const nextLine = cleanedSignature[i + 1];
    
    if (/^(testing|test|meeting|sprint|planning|discussion|update|review|feedback|follow[\s-]?up)/i.test(currentLine)) {
      continue;
    }
    
    const combined = `${currentLine} ${nextLine}`;
    const combinedMatch = combined.match(/^([A-Z][a-zA-Z]+(?:Test|Automation)[a-zA-Z\s]*(?:Client|Company|No.*Client))/i);
    
    if (combinedMatch && combinedMatch[1]) {
      const companyName = combinedMatch[1].trim();
      if (companyName.length >= 10 && companyName.length <= 100) {
        return companyName;
      }
    }
    
    if (i < cleanedSignature.length - 2) {
      const thirdLine = cleanedSignature[i + 2];
      const combinedThree = `${currentLine}${nextLine} ${thirdLine}`;
      const threeLineMatch = combinedThree.match(/^([A-Z][a-zA-Z]+(?:Test|Automation)[a-zA-Z\s]*(?:Client|Company|No.*Client))/i);
      
      if (threeLineMatch && threeLineMatch[1]) {
        const companyName = threeLineMatch[1].trim();
        if (companyName.length >= 10 && companyName.length <= 100) {
          return companyName;
        }
      }
    }
  }
  
  // Single line with "Client" keyword
  const clientKeywordPattern = /^([A-Z][a-zA-Z\s&,.-]+\s+Client)$/i;
  for (const line of cleanedSignature) {
    const clientMatch = line.match(clientKeywordPattern);
    if (clientMatch && clientMatch[1]) {
      const companyName = clientMatch[1].trim();
      if (companyName.length >= 5 && companyName.length <= 100) {
        const excludeWords = ['Email', 'Phone', 'Mobile', 'Address', 'Website'];
        const isExcluded = excludeWords.some(word => companyName.includes(word));
        
        if (!isExcluded) {
          return companyName;
        }
      }
    }
  }
  
  // Standalone company with legal suffix
  const standaloneLegalSuffixes = /^([A-Z][a-zA-Z\s&,.-]+(?:Inc\.?|LLC|L\.L\.C\.|Corp\.?|Corporation|Company|Co\.|Ltd\.?|Limited|LP|L\.P\.|LLP|L\.L\.P\.))$/;
  
  for (const line of cleanedSignature) {
    const standaloneMatch = line.match(standaloneLegalSuffixes);
    if (standaloneMatch && standaloneMatch[1]) {
      const companyName = standaloneMatch[1].trim();
      
      if (companyName.length >= 5 && companyName.length <= 100) {
        const excludeWords = ['Email', 'Phone', 'Mobile', 'Address', 'Website', 'The Company'];
        const isExcluded = excludeWords.some(word => companyName.includes(word));
        
        if (!isExcluded) {
          return companyName;
        }
      }
    }
  }
  
  // Multi-word capitalized names
  const multiWordPattern = /^([A-Z][a-zA-Z]+(?:\s+[A-Z][a-zA-Z]+){1,4})$/;
  for (const line of cleanedSignature) {
    const personNamePattern = /^[A-Z][a-z]+\s+[A-Z][a-z]+$/;
    if (personNamePattern.test(line)) {
      continue;
    }
    
    const multiWordMatch = line.match(multiWordPattern);
    if (multiWordMatch && multiWordMatch[1]) {
      const possibleCompany = multiWordMatch[1].trim();
      
      const wordCount = possibleCompany.split(/\s+/).length;
      const hasClientKeywords = /\b(client|automation|test|company|group|partners|associates|solutions|services)\b/i.test(possibleCompany);
      
      if ((wordCount >= 3 || hasClientKeywords) && possibleCompany.length >= 10 && possibleCompany.length <= 100) {
        const excludeWords = ['Email', 'Phone', 'Mobile', 'Address', 'Website', 'Best Regards', 'Thank You'];
        const isExcluded = excludeWords.some(word => possibleCompany.includes(word));
        
        if (!isExcluded) {
          return possibleCompany;
        }
      }
    }
  }
  
  // Context patterns
  const contextPatterns = [
    { titlePattern: /(?:Manager|Director|Associate|Senior|Engineer|Developer|Analyst|VP|President|CEO|CTO|CFO|Partner|Principal|Architect|Lead|Head)/i, nextLine: true },
    { pattern: /(?:at|with|for)\s+([A-Z][a-zA-Z\s&,.-]+(?:(?:Inc\.?|LLC|Corp\.?|Corporation|Company|Co\.|Ltd\.?|Limited)|(?:\s+Client)|(?:[A-Z][a-zA-Z]+){2,}))/i },
    { pattern: /<(?:b|strong)>([A-Z][a-zA-Z\s&,.-]+(?:Inc\.?|LLC|Corp\.?|Corporation|Company|Co\.|Ltd\.?|Limited|Client))<\/(?:b|strong)>/i }
  ];
  
  for (let i = 0; i < cleanedSignature.length - 1; i++) {
    const line = cleanedSignature[i];
    const nextLine = cleanedSignature[i + 1];
    
    if (contextPatterns[0].titlePattern.test(line)) {
      const companyMatch = nextLine.match(standaloneLegalSuffixes) || 
                          nextLine.match(clientKeywordPattern) ||
                          nextLine.match(multiWordPattern);
      if (companyMatch && companyMatch[1]) {
        const companyName = companyMatch[1].trim();
        if (companyName.length >= 5 && companyName.length <= 100) {
          return companyName;
        }
      }
    }
  }
  
  for (const line of cleanedSignature) {
    for (const { pattern } of contextPatterns.filter(p => p.pattern)) {
      const match = line.match(pattern);
      if (match && match[1]) {
        const companyName = match[1].trim();
        if (companyName.length >= 5 && companyName.length <= 100) {
          const excludeWords = ['Email', 'Phone', 'Mobile', 'Address', 'Website'];
          const isExcluded = excludeWords.some(word => companyName.includes(word));
          if (!isExcluded) {
            return companyName;
          }
        }
      }
    }
  }
  
  // Broader fallback search
  const broadCompanyPatterns = [
    /([A-Z][a-zA-Z]*(?:Test|Automation)[a-zA-Z\s<>/]*(?:No|With)?[a-zA-Z\s<>/]*Retainer[a-zA-Z\s<>/]*Client)/i,
    /([A-Z][a-zA-Z\s&,.-]+\s+Client)/i,
    /([A-Z][a-zA-Z\s&,.-]{3,}(?:Inc\.?|LLC|L\.L\.C\.|Corp\.?|Corporation|Company|Co\.|Ltd\.?|Limited|LP|L\.P\.|LLP|L\.L\.P\.))/,
    /(?:working for|employed by|work at|at)\s+([A-Z][a-zA-Z\s&,.-]+(?:Inc\.?|LLC|L\.L\.C\.|Corp\.?|Corporation|Company|Co\.|Ltd\.?|Limited|Client))/i
  ];
  
  for (const pattern of broadCompanyPatterns) {
    const match = signatureText.match(pattern);
    if (match && match[1]) {
      const companyName = match[1].trim()
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      
      if (companyName.length >= 5 && companyName.length <= 100) {
        const excludePatterns = [
          /^(the|and|or|at|in|on|for|with|by)$/i,
          /^(email|phone|mobile|fax|address|website)$/i,
          /^(sincerely|regards|best|thanks|thank you)$/i
        ];
        
        const isExcluded = excludePatterns.some(exclude => exclude.test(companyName));
        if (!isExcluded) {
          return companyName;
        }
      }
    }
  }
  
  return null;
}

export async function quickSearchByFileId(fileId, options = {}) {
  return assignmentSearchService.searchAssignments({ fileId }, options);
}

/**
 * Multi-criteria search
 */
export async function searchAssignmentsMultiCriteria(criteria, options = {}) {
  return assignmentSearchService.searchAssignments(criteria, options);
}

/**
 * Get search service instance for advanced usage
 */
export function getSearchService() {
  return assignmentSearchService;
}

export default assignmentSearchService;